// Data-driven audio manager for web and mobile
interface AudioEventDef {
  type: "ui" | "sfx" | "music";
  files: {
    ogg: string;
    m4a: string;
  };
  volume: number;
  duration: number;
  loop?: boolean;
  loopStart?: number;
  loopEnd?: number;
  note?: string;
}

interface AudioManifest {
  events: Record<string, AudioEventDef>;
}

import audioManifestJson from "../../audio/audio.json";
const audioManifest = audioManifestJson as AudioManifest;

export interface AudioEvent {
  type: "ui" | "sfx" | "music";
  files: {
    ogg: string;
    m4a: string;
  };
  volume: number;
  duration: number;
  loop?: boolean;
  loopStart?: number;
  loopEnd?: number;
  note?: string;
}

export type AudioFormat = "ogg" | "m4a";

export class AudioManager {
  private format: AudioFormat;
  private context: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<void>>();
  private sources = new Map<string, AudioBufferSourceNode>();
  private gainNodes = new Map<string, GainNode>();
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private masterGain: GainNode | null = null;
  private musicMuted = false;
  private sfxMuted = false;
  private unlocked = false;
  private currentMusic: string | null = null;

  constructor() {
    // Detect format based on platform
    const audio = document.createElement("audio");
    this.format = audio.canPlayType("audio/ogg") ? "ogg" : "m4a";
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;

    try {
      this.context = new AudioContext();

      // Create gain nodes
      this.masterGain = this.context.createGain();
      this.musicGain = this.context.createGain();
      this.sfxGain = this.context.createGain();

      this.musicGain.connect(this.masterGain);
      this.sfxGain.connect(this.masterGain);
      this.masterGain.connect(this.context.destination);

      // Resume context if suspended
      if (this.context.state === "suspended") {
        await this.context.resume();
      }

      this.unlocked = true;
    } catch (error: unknown) {
      console.error(
        "Failed to unlock audio:",
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  async preload(ids: string[]): Promise<void> {
    if (!this.context) await this.unlock();
    if (!this.context) return;

    const loadPromises = ids.map(async (id) => {
      if (this.buffers.has(id)) return;
      const existing = this.loading.get(id);
      if (existing) return existing;

      const loading = this.loadEvent(id).finally(() => {
        this.loading.delete(id);
      });
      this.loading.set(id, loading);
      return loading;
    });

    await Promise.all(loadPromises);
  }

  private async loadEvent(id: string): Promise<void> {
    const event = audioManifest.events[id];
    if (!event) {
      console.warn(`Audio event not found: ${id}`);
      return;
    }

    const filename = event.files[this.format];
    const path = `/audio/${filename}`;

    try {
      const response = await fetch(path);
      if (!response.ok) {
        console.warn(`Audio file not found: ${path}`);
        return;
      }
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context!.decodeAudioData(arrayBuffer);
      this.buffers.set(id, audioBuffer);
    } catch (error: unknown) {
      console.warn(`Failed to load audio: ${path}`, error);
    }
  }

  play(
    id: string,
    options: { pitchShift?: number; forceRestart?: boolean } = {},
  ): void {
    if (!this.context || !this.unlocked) return;

    const event = audioManifest.events[id];
    if (!event) return;

    const buffer = this.buffers.get(id);
    if (!buffer) return;

    // Check if muted
    const isMuted = event.type === "music" ? this.musicMuted : this.sfxMuted;
    if (isMuted) return;

    // Stop existing source for this id if forceRestart or if it's music
    if (options.forceRestart || event.type === "music") {
      this.stop(id);
    }

    // Create source
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = Boolean(event.loop);

    if (
      event.loop &&
      typeof event.loopStart === "number" &&
      typeof event.loopEnd === "number"
    ) {
      source.loopStart = event.loopStart;
      source.loopEnd = event.loopEnd;
    }

    // Apply pitch shift (for roll_tier variations)
    if (options.pitchShift) {
      source.playbackRate.value = Math.pow(2, options.pitchShift / 12);
    }

    // Create gain node for this source
    const gainNode = this.context.createGain();
    gainNode.gain.value = event.volume;
    source.connect(gainNode);

    // Connect to appropriate bus
    const busGain = event.type === "music" ? this.musicGain : this.sfxGain;
    gainNode.connect(busGain!);

    // Start playback
    source.start(0);

    // Store references
    this.sources.set(id, source);
    this.gainNodes.set(id, gainNode);

    // Track current music
    if (event.type === "music") {
      this.currentMusic = id;
    }

    // Clean up when finished
    source.onended = () => {
      this.sources.delete(id);
      this.gainNodes.delete(id);
      if (this.currentMusic === id) {
        this.currentMusic = null;
      }
    };
  }

  stop(id: string): void {
    const source = this.sources.get(id);
    if (source) {
      try {
        source.stop();
      } catch {
        // Already stopped
      }
      this.sources.delete(id);
      this.gainNodes.delete(id);
      if (this.currentMusic === id) {
        this.currentMusic = null;
      }
    }
  }

  stopAll(): void {
    for (const id of this.sources.keys()) {
      this.stop(id);
    }
  }

  stopMusic(): void {
    if (this.currentMusic) {
      this.stop(this.currentMusic);
    }
  }

  setMusicVolume(volume: number): void {
    if (this.musicGain) {
      this.musicGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  setSfxVolume(volume: number): void {
    if (this.sfxGain) {
      this.sfxGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  setMusicMuted(muted: boolean): void {
    this.musicMuted = muted;
    if (muted && this.currentMusic) {
      this.stop(this.currentMusic);
    }
  }

  setSfxMuted(muted: boolean): void {
    this.sfxMuted = muted;
  }

  getMusicMuted(): boolean {
    return this.musicMuted;
  }

  getSfxMuted(): boolean {
    return this.sfxMuted;
  }

  toggleMute(): void {
    const newState = !(this.musicMuted && this.sfxMuted);
    this.setMusicMuted(newState);
    this.setSfxMuted(newState);
  }

  pause(): void {
    if (this.context && this.context.state === "running") {
      void this.context.suspend();
    }
  }

  resume(): void {
    if (this.context && this.context.state === "suspended") {
      void this.context.resume();
    }
  }
}
